import { ConnectionKind, ModelConstants } from 'shell-models-lib';

/**
 * Read the base URL (`path`) advertised by a linked service's stringified
 * `datasourceReference`. Returns `undefined` when `raw` is missing, malformed,
 * or carries no plain-string path. Used as a fallback when the LS's own
 * typed `url` field has not been hydrated yet (the Trident commit path
 * populates the DSR before the typed attributes).
 */
export function extractDsrPathFromRawJson(raw: string | undefined): string | undefined {
    if (!raw) {
        return undefined;
    }
    try {
        const outer = JSON.parse(raw);
        const dsrJson = outer?.properties?.datasourceReference;
        if (!dsrJson || typeof dsrJson !== 'string') {
            return undefined;
        }
        const path = JSON.parse(dsrJson)?.path;
        return typeof path === 'string' && path.length > 0 ? path : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Creates a JSON Data Source Reference (DSR) string for different types of Fabric artifacts
 * @param linkedServiceType - The linkedService or artifact type of Fabric artifact ('lakehouse', 'datawarehouse', 'fabric-sql', 'kusto-database')
 * @param artifactId - The unique identifier of the artifact
 * @param workspaceId - The workspace ID containing the artifact
 * @returns JSON string containing the DSR configuration
 * @throws Error if artifact type is not supported or for direct KQL fabric items
 *
 * Duplicated from https://msazure.visualstudio.com/One/_git/PowerQuery-Web?path=/src/Product/UI/ClientShared/Scripts/FabricApis/FabricArtifactDsrUtils.ts&version=GBmaster
 */
export function createJsonDsrForFabricArtifact(linkedServiceType: string, artifactId: string, workspaceId: string) {
    switch (linkedServiceType.toLowerCase()) {
        // Handle Lakehouse artifact type
        case ModelConstants.lakehouseArtifactType.toLowerCase():
            return JSON.stringify({
                protocol: 'lakehouse',
                address: {
                    workspace: workspaceId,
                    lakehouse: artifactId
                },
                authentication: null,
                query: null
            });

        // Handle Data Warehouse artifact type
        case ConnectionKind.WARHOUSE.toLowerCase():
        case ModelConstants.dataWarehouseArtifactType.toLowerCase():
            return JSON.stringify({
                protocol: 'fabric-warehouse',
                address: {
                    workspace: workspaceId,
                    warehouse: artifactId
                },
                authentication: null,
                query: null
            });

        // Handle Fabric SQL artifact type
        case 'FabricSqlDatabase'.toLowerCase():
        case ModelConstants.fabricSqlArtifactType.toLowerCase():
            return JSON.stringify({
                protocol: 'fabric-sql',
                address: {
                    workspace: workspaceId,
                    warehouse: artifactId
                },
                authentication: null,
                query: null
            });

        // Handle Kusto Database artifact type - not supported for direct DSR creation
        case ModelConstants.kustoDatabaseArtifactType.toLowerCase(): {
            throw new Error(`Cannot create DSR directly using KQL fabric item other than its artifact item.`);
        }

        default:
            throw new Error(`Cannot create DSR for artifacts ${linkedServiceType} other than LH/DW/KQL.`);
    }
}

// export function createJsonDsrForFabricKqlArtifactItem(fabricKqlArtifactItem: FabricKqlArtifactItem): string {
//     return JSON.stringify({
//         protocol: "x-datasource",
//         address: {
//             kind: "AzureDataExplorer",
//             path: fabricKqlArtifactItem.properties.queryServiceUri,
//         },
//         authentication: null,
//         query: null,
//     });
// }
